import React, { FunctionComponent, useCallback, useEffect, useState } from 'react';

import { cloneDeep } from 'lodash';
import { Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { trackPromise } from 'react-promise-tracker';

import { Overlay } from 'core/overlay/Overlay';
import { ReviewCard } from 'reviews2/feed/review-yeti/card/ReviewCard';
import { useReview } from 'reviews2/feed/review-yeti/Review.store';
import { ContextProps, ReadOnlyReason, ReviewProps } from 'reviews2/feed/review-yeti/Review.types';
import { getReviewHistory } from 'reviews2/Reviews.api';

import 'reviews2/feed/review-yeti/history/review-history/ReviewHistory.scss';

interface ReviewHistoryModalProps {
    context: ReviewProps;
    show: (show: boolean) => void;
}

export const ReviewHistory: FunctionComponent<ContextProps> = ({ context }) => {
    const { t } = useTranslation('reviews2');
    const [{ review }] = useReview();
    const [history, setHistory] = useState<any[]>();
    const PROMISE_AREA = `r4e-review-history-review-${review?.document_id}`;

    const fetchHistory = useCallback(async () => {
        const args = {
            includePage: true,
            includeLabels: true,
            includeSource: true,
            includeLocation: true,
            includeCredentials: true,
            includeCredentialStatus: true,
            includeAttributionUsers: true,
        };
        const history = await trackPromise(
            getReviewHistory(review.r4e_tenant_id, review.document_id, args),
            PROMISE_AREA,
        );

        setHistory(history);
    }, [review, PROMISE_AREA]);

    useEffect(() => {
        if (review) {
            fetchHistory();
        }
    }, [review, fetchHistory]);

    return (
        <div className="r4e-review-history">
            <div className="history">
                <Overlay area={PROMISE_AREA} />
                {history?.length ? (
                    history?.map((history) => {
                        return (
                            <div key={history.updatedDate} className="review-history">
                                <ReviewCard
                                    context={{
                                        ...context,
                                        review: {
                                            ...history,
                                        },
                                    }}
                                />
                            </div>
                        );
                    })
                ) : (
                    <div className="no-history">
                        <p className="text-center">{t('NO_HISTORY')}</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export const ReviewHistoryModal: FunctionComponent<ReviewHistoryModalProps> = ({ context, show }) => {
    const { t } = useTranslation('reviews2');
    const modifiedContext: ReviewProps = cloneDeep(context);

    if (modifiedContext.options?.historyOptions?.showHistory) {
        modifiedContext.options.historyOptions.showHistory = false;
        modifiedContext.options.readOnly = true;
        modifiedContext.options.readOnlyReason = ReadOnlyReason.View;
        modifiedContext.options.showReplies = false;
    }

    return (
        <Modal show={show} contentClassName="r4e-review-history-modal" size="lg" onHide={() => show(false)}>
            <Modal.Header closeButton>
                <Modal.Title>{t(`REVIEW_HISTORY`)}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <ReviewHistory context={modifiedContext} />
            </Modal.Body>
        </Modal>
    );
};
